import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { HiOutlineGlobeAlt, HiChevronDown, HiMenu, HiX } from "react-icons/hi";
import { HiOutlineFlag } from "react-icons/hi2";
import { usePublicPage } from "../../../hooks/usePublicPage";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import { getApiErrorMessage } from "../../../utils/apiResponse";
import { useLanguage } from "../../../context/LanguageContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePrefetchPublicRoute } from "../../../hooks/usePrefetchPublicRoute";
import { getLanguageMeta } from "../../../utils/languages";
import "./Navbar.css";

const buildNavbarModel = (section, language) => {
  const fields = section.fields || {};
  const logo =
    section.media?.groups?.logo?.[0] ||
    section.media?.primary ||
    null;

  if (!logo?.url) {
    throw new Error("Navbar logo media is missing from the API response.");
  }

  const links = [
    { to: fields.aboutUrl, label: fields.aboutLabel },
    { to: fields.lawUrl, label: fields.lawLabel },
    { to: fields.newsUrl, label: fields.newsLabel },
  ];

  for (const link of links) {
    if (!link.to || !link.label) {
      throw new Error("Navbar link fields are incomplete in the API response.");
    }
  }

  if (!fields.reportButtonText || !fields.reportButtonUrl) {
    throw new Error("Navbar report button fields are incomplete in the API response.");
  }

  const meta = getLanguageMeta(language);

  return {
    logoUrl: resolveMediaUrl(logo.url),
    logoAlt: fields.logoAlt || "",
    links,
    reportText: fields.reportButtonText,
    reportUrl: fields.reportButtonUrl,
    languageLabel: meta.short,
  };
};

const LanguageSwitcher = ({ open, onToggle, onSelect, onClose }) => {
  const { language, languages } = useLanguage();
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };

    // Use click (not mousedown) so option selection is not cancelled by outside-close.
    document.addEventListener("click", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("click", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  const choose = (event, code) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect(code);
  };

  return (
    <div className="navbar__lang" ref={rootRef}>
      <button
        type="button"
        className={`navbar__lang-btn ${open ? "navbar__lang-btn--open" : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
      >
        <HiOutlineGlobeAlt />
        <span>{getLanguageMeta(language).short}</span>
        <HiChevronDown className="navbar__lang-chevron" />
      </button>

      {open ? (
        <ul className="navbar__lang-menu" role="listbox" aria-label="Languages">
          {languages.map((lang) => (
            <li key={lang.code} role="option" aria-selected={lang.code === language}>
              <button
                type="button"
                className={`navbar__lang-option ${
                  lang.code === language ? "navbar__lang-option--active" : ""
                }`}
                onMouseDown={(event) => choose(event, lang.code)}
                onClick={(event) => choose(event, lang.code)}
              >
                <span className="navbar__lang-option-short">{lang.short}</span>
                <span className="navbar__lang-option-label">{lang.label}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const Navbar = () => {
  const { language, setLanguage, meta } = useLanguage();
  const { t } = useTranslation();
  const prefetchRoute = usePrefetchPublicRoute();
  const pageQuery = usePublicPage("shared");
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopLangOpen, setDesktopLangOpen] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { model, buildError } = useMemo(() => {
    if (!pageQuery.data) {
      return { model: null, buildError: null };
    }

    try {
      const navbar = pageQuery.data.sections?.find((s) => s.key === "navbar");
      if (!navbar) {
        throw new Error("Navbar section was not found on the shared page.");
      }
      return { model: buildNavbarModel(navbar, language), buildError: null };
    } catch (err) {
      return {
        model: null,
        buildError: err instanceof Error ? err.message : String(err),
      };
    }
  }, [pageQuery.data, language]);

  const navLinks = useMemo(
    () => [
      ...(model?.links || []),
      { to: "/track", label: t("navbar.trackCase") },
    ],
    [model?.links, t]
  );

  const error = pageQuery.error != null
    ? getApiErrorMessage(pageQuery.error)
    : buildError;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 2);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const prefetchHandlers = (pathname) => ({
    onMouseEnter: () => prefetchRoute(pathname),
    onFocus: () => prefetchRoute(pathname),
  });

  const closeMenu = () => {
    setMenuOpen(false);
    setMobileLangOpen(false);
  };

  const handleSelectLanguage = (code) => {
    setLanguage(code);
    setDesktopLangOpen(false);
    setMobileLangOpen(false);
    setMenuOpen(false);
  };

  const showShell = pageQuery.isPending && !model;

  if (showShell) {
    return (
      <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""}`} dir={meta.dir}>
        <div className="navbar__container">
          <div className="navbar__logo" aria-busy="true">
            <span style={{ color: "#64748b", fontSize: 14 }}>Loading navigation…</span>
          </div>
          <div className="navbar__actions navbar__actions--desktop">
            <LanguageSwitcher
              open={desktopLangOpen}
              onToggle={() => setDesktopLangOpen((v) => !v)}
              onSelect={handleSelectLanguage}
              onClose={() => setDesktopLangOpen(false)}
            />
          </div>
        </div>
      </nav>
    );
  }

  if (error || !model) {
    return (
      <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""}`} dir={meta.dir}>
        <div className="navbar__container">
          <div className="navbar__logo" role="alert">
            <span style={{ color: "#b91c1c", fontSize: 14 }}>
              {error || "Navbar could not be loaded."}
            </span>
          </div>
          <div className="navbar__actions navbar__actions--desktop">
            <LanguageSwitcher
              open={desktopLangOpen}
              onToggle={() => setDesktopLangOpen((v) => !v)}
              onSelect={handleSelectLanguage}
              onClose={() => setDesktopLangOpen(false)}
            />
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={`navbar ${menuOpen ? "navbar--menu-open" : ""} ${scrolled ? "navbar--scrolled" : ""}`}
      dir={meta.dir}
    >
      <div className="navbar__container">
        <Link
          to="/"
          className="navbar__logo"
          onClick={closeMenu}
          {...prefetchHandlers("/")}
        >
          <img src={model.logoUrl} alt={model.logoAlt} />
        </Link>

        <div className="navbar__links navbar__links--desktop">
          {navLinks.map((link) => (
            <Link
              key={`${link.to}-${link.label}`}
              to={link.to}
              className="navbar__link"
              {...prefetchHandlers(link.to)}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="navbar__actions navbar__actions--desktop">
          <LanguageSwitcher
            open={desktopLangOpen}
            onToggle={() => setDesktopLangOpen((v) => !v)}
            onSelect={handleSelectLanguage}
            onClose={() => setDesktopLangOpen(false)}
          />

          <Link
            to={model.reportUrl}
            className="navbar__report-btn"
            {...prefetchHandlers(model.reportUrl)}
          >
            <HiOutlineFlag />
            <span>{model.reportText}</span>
          </Link>
        </div>

        <button
          type="button"
          className={`navbar__hamburger ${menuOpen ? "navbar__hamburger--open" : ""}`}
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={menuOpen}
        >
          <HiMenu />
        </button>
      </div>

      <div
        className={`navbar__overlay ${menuOpen ? "navbar__overlay--open" : ""}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      <div
        className={`navbar__mobile-menu ${menuOpen ? "navbar__mobile-menu--open" : ""}`}
      >
        <div className="navbar__mobile-header">
          <Link
            to="/"
            className="navbar__mobile-logo"
            onClick={closeMenu}
            {...prefetchHandlers("/")}
          >
            <img src={model.logoUrl} alt={model.logoAlt} />
          </Link>
          <button
            type="button"
            className="navbar__close"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <HiX />
          </button>
        </div>

        <div className="navbar__mobile-body">
          <div className="navbar__mobile-links">
            {navLinks.map((link) => (
              <Link
                key={`${link.to}-${link.label}-mobile`}
                to={link.to}
                className="navbar__mobile-link"
                onClick={closeMenu}
                {...prefetchHandlers(link.to)}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="navbar__mobile-actions">
            <LanguageSwitcher
              open={mobileLangOpen}
              onToggle={() => setMobileLangOpen((v) => !v)}
              onSelect={handleSelectLanguage}
              onClose={() => setMobileLangOpen(false)}
            />

            <Link
              to={model.reportUrl}
              className="navbar__report-btn"
              onClick={closeMenu}
              {...prefetchHandlers(model.reportUrl)}
            >
              <HiOutlineFlag />
              <span>{model.reportText}</span>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
